import { Fragment, useCallback } from "react";
import { Eye, ChevronDown, ChevronUp, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { FilePreviewInline } from "@/components/file-preview/FilePreviewInline";
import { useFilePreview } from "@/components/file-preview/hooks";
import { getAttachmentFileUrl } from "@/lib/utils/getAttachmentFileUrl";
import { downloadFileFromUrl } from "@/lib/file";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { buildAttachmentInfoParts } from "@/lib/utils/buildAttachmentInfoParts";
import { AttachmentsNoFilesMessage } from "@/components/AttachmentsNoFilesMessage";

export interface AttachmentFileItem {
  filename: string;
  path?: string;
  url?: string;
  size?: string;
  confirmed_at?: string | null;
}

export interface AttachmentGroup {
  key: string;
  title: string;
  files: AttachmentFileItem[];
}

interface AttachmentsDisplaySectionProps {
  groups: AttachmentGroup[];
  attachmentType?: string;
  canRead?: boolean;
}

export const AttachmentsDisplaySection = ({
  groups,
  attachmentType,
  canRead = true,
}: AttachmentsDisplaySectionProps) => {
  const {
    preview,
    expandedKeys,
    toggleInlinePreview,
    openPreviewDialog,
    closePreview,
  } = useFilePreview();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleDownload = useCallback(
    async (fileUrl: string | null, filename: string) => {
      if (!fileUrl) return;
      try {
        await downloadFileFromUrl(fileUrl, filename);
      } catch (error) {
        console.error("Failed to download file:", error);
        toast({
          title: t("errors.genericTitle"),
          description: t("errors.downloadFileFailed"),
          variant: "destructive",
        });
      }
    },
    [toast, t],
  );

  const hasAnyFiles = groups.some((g) => g.files.length > 0);

  const renderFileRow = useCallback(
    (file: AttachmentFileItem, rowKey: string) => {
      const fileUrl = getAttachmentFileUrl(file);
      const disabled = !canRead || !fileUrl;
      const isExpanded = expandedKeys.has(rowKey);
      const infoParts = buildAttachmentInfoParts({
        confirmed_at: file.confirmed_at,
        size: file.size,
        filePath: file.path ?? file.filename,
      });

      return (
        <Fragment key={rowKey}>
          <tr
            className={cn(
              "odd:bg-white even:bg-gray-100/60 transition-colors duration-150 border-b border-gray-200/80",
              !disabled && "hover:bg-blue-100/40",
            )}
          >
            <td className="px-2 py-1 align-top sm:px-3 sm:py-1.5">
              <div className="flex flex-col gap-1 sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-2 min-w-0">
                <span
                  className="text-xs text-gray-900 truncate min-w-0"
                  title={file.filename}
                >
                  {file.filename}
                </span>
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 min-w-0 sm:contents">
                  <span className="text-xs text-gray-500 break-words min-w-0">
                    {infoParts.length > 0 ? infoParts.join(" | ") : "-"}
                  </span>
                  <div className="flex items-center gap-0 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-6 sm:w-6 touch-manipulation disabled:pointer-events-auto disabled:cursor-not-allowed"
                      onClick={() => handleDownload(fileUrl, file.filename)}
                      disabled={disabled}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-6 sm:w-6 touch-manipulation disabled:pointer-events-auto disabled:cursor-not-allowed"
                      onClick={() => openPreviewDialog(fileUrl, file.filename)}
                      disabled={disabled}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-6 sm:w-6 touch-manipulation disabled:pointer-events-auto disabled:cursor-not-allowed"
                      onClick={() => toggleInlinePreview(rowKey)}
                      disabled={disabled}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
          {isExpanded && fileUrl && (
            <tr>
              <td className="p-0">
                <div className="border-t border-gray-200 bg-gray-50/50 px-3 py-2">
                  <FilePreviewInline
                    fileUrl={fileUrl}
                    fileName={file.filename}
                  />
                </div>
              </td>
            </tr>
          )}
        </Fragment>
      );
    },
    [
      canRead,
      expandedKeys,
      handleDownload,
      openPreviewDialog,
      toggleInlinePreview,
    ],
  );

  if (!hasAnyFiles) {
    return <AttachmentsNoFilesMessage attachmentType={attachmentType} />;
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => {
        if (group.files.length === 0) return null;

        const groupFiles = group.files.map((file, fileIndex) => {
          const rowKey = `${group.key}-${file.filename}-${fileIndex}`;
          return renderFileRow(file, rowKey);
        });

        return (
          <div key={group.key} className="overflow-x-auto">
            <table className="w-full min-w-0 border-collapse">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left sm:px-3 sm:py-1.5 border-b-2 border-gray-300">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">
                      {group.title}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>{groupFiles}</tbody>
            </table>
          </div>
        );
      })}
      {preview?.fileUrl && (
        <FilePreviewDialog
          open
          onClose={closePreview}
          fileUrl={preview.fileUrl}
          fileName={preview.fileName}
        />
      )}
    </div>
  );
};
